import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, FileText, PlusSquare, User, Heart, FileImage, MapPin, Stethoscope, Activity, CirclePlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../utils/permissions";

// Moved FormSection outside the main component to prevent re-rendering issues
const FormSection = ({ title, isOpen, onToggle, icon: Icon, children }) => (
  <div className="form-section-container">
    <div className="form-section-header" onClick={onToggle} role="button" tabIndex={0}>
      <div className="header-content">
        {Icon && <Icon className="icon" size={20} />}
        <h2 className="section-title">{title}</h2>
      </div>
      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </div>
    {isOpen && <div className="form-section-body">{children}</div>}
  </div>
);

const NewEntry = ({ onSubmit }) => {
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('session_token') || '';
  const authHeaders = () => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // Replace your current formData with this:
  const [formData, setFormData] = useState({
    patientType: "NEW",
    patient_date: new Date().toISOString().split("T")[0],
    name: "",
    mobile: "",
    mobile2: "",
    email: "",
    age: "",
    sex: "M",
    weight: "",
    height: "",
    bp: 0,
    diabetes: 0,
    thyroid: 0,
    remark: "",
    remark1: "",
    remark4: "",
    remark4_1: "",
    diagnosis: "",
    // diagnosisCode: "",
    diagnosisSymptom: "",
    diagnosis1: "",
    // diagnosisCode1: "",
    diagnosisSymptom1: "",
    address: "",
    pincode: "",
    city: "",
    state: "",
    country: "India",
    serviceType: "VISIT",
    selectedSymptoms: [],
    selectedSymptoms1: [],
    patient_pic: null,

  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Updated mobile validation state to support multiple matches and auto-link flag
  const [mobileValidation, setMobileValidation] = useState({
    isValidating: false,
    isValid: null,
    message: "",
    existingPatients: [],
    autoLinkOnCreate: false,
    lead: null, // Store lead information if found
    hasLead: false
  });

  // Store lead_id for auto-transfer
  const [leadId, setLeadId] = useState(null);

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Diagnosis and symptoms state
  const [diagnoses, setDiagnoses] = useState([]);
  const [showSecondDiagnosis, setShowSecondDiagnosis] = useState(false);

  const [symptoms, setSymptoms] = useState([]);
  const [symptoms1, setSymptoms1] = useState([]);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(true);
  const [loadingSymptoms, setLoadingSymptoms] = useState(false);
  const [loadingSymptoms1, setLoadingSymptoms1] = useState(false);
  // ✅ ADDED: products state
  const [products, setProducts] = useState([]);
  const [products1, setProducts1] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingProducts1, setLoadingProducts1] = useState(false);
  const [isPatientInfoOpen, setIsPatientInfoOpen] = useState(true);
  const [isDiagnosisInfoOpen, setIsDiagnosisInfoOpen] = useState(true);
  const [isAddressInfoOpen, setIsAddressInfoOpen] = useState(true);
  // const [productsByDiagnosis, setProductsByDiagnosis] = useState([]);
  // const [productsByDiagnosis1, setProductsByDiagnosis1] = useState([]);
  const [selectedMedicines1, setSelectedMedicines1] = useState([]);
  const [selectedMedicines, setSelectedMedicines] = useState([]);

  // Fetch diagnoses on component mount
  useEffect(() => {
    const fetchDiagnoses = async () => {
      try {
        const response = await fetch(
          "https://newerp.awasthivitality.com/api/diagnosis/options"
        );
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setDiagnoses(data.data);
        }
      } catch (error) {
        console.error("Error fetching diagnoses:", error);
      } finally {
        setLoadingDiagnoses(false);
      }
    };

    fetchDiagnoses();
  }, []);
  useEffect(() => {
    const fetchSymptoms = async () => {
      if (!formData.diagnosis) {
        setSymptoms([]);
        return;
      }

      setLoadingSymptoms(true);
      try {
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/diagnosis/${formData.diagnosis}/symptoms`
        );
        const data = await response.json();

        if (data.success && Array.isArray(data.data?.symptoms)) {
          setSymptoms(data.data.symptoms);
        } else {
          setSymptoms([]);
        }
      } catch (error) {
        console.error("Error fetching symptoms:", error);
        setSymptoms([]);
      } finally {
        setLoadingSymptoms(false);
      }
    };

    fetchSymptoms();
  }, [formData.diagnosis]);
  useEffect(() => {
    const fetchProducts = async () => {
      if (!formData.diagnosis) {
        setProducts([]);
        setSelectedMedicines([]);
        return;
      }

      setLoadingProducts(true);
      try {
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/products-by-diagnosis/${formData.diagnosis}`,
          { headers: authHeaders() }
        );

        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          setProducts(data.data);
          setSelectedMedicines([]);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Product fetch error:", error);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [formData.diagnosis]);
  useEffect(() => {
    const fetchSymptoms1 = async () => {
      if (!formData.diagnosis1) {
        setSymptoms1([]);
        return;
      }

      setLoadingSymptoms1(true);
      try {
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/diagnosis/${formData.diagnosis1}/symptoms`
        );
        const data = await response.json();

        if (data.success && Array.isArray(data.data?.symptoms)) {
          setSymptoms1(data.data.symptoms);
        } else {
          setSymptoms1([]);
        }
      } catch (error) {
        console.error("Error fetching symptoms 1:", error);
        setSymptoms1([]);
      } finally {
        setLoadingSymptoms1(false);
      }
    };

    fetchSymptoms1();
  }, [formData.diagnosis1]);
  useEffect(() => {
    const fetchProducts1 = async () => {
      if (!formData.diagnosis1) {
        setProducts1([]);
        setSelectedMedicines1([]);
        return;
      }

      try {
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/products-by-diagnosis/${formData.diagnosis1}`,
          { headers: authHeaders() }
        );

        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          setProducts1(data.data);
          setSelectedMedicines1([]);
        } else {
          setProducts1([]);
        }
      } catch (error) {
        console.error("Product fetch error 1:", error);
        setProducts1([]);
      }
    };

    fetchProducts1();
  }, [formData.diagnosis1]);
  // ✅ ADDED: Fetch products for second diagnosis
  // useEffect(() => {
  //   const fetchProducts1 = async () => {
  //     if (!formData.diagnosis1) {
  //       setProducts1([]);
  //       return;
  //     }

  //     setLoadingProducts1(true);
  //     try {
  //       const response = await fetch(
  //         `https://newerp.awasthivitality.com/api/products-by-diagnosis/${formData.diagnosis1}`,
  //         { headers: authHeaders() }
  //       );
  //       const data = await response.json();
  //       if (data.success && Array.isArray(data.data)) {
  //         setProducts1(data.data);
  //       } else {
  //         setProducts1([]);
  //       }
  //     } catch (err) {
  //       console.error("Product fetch error", err);
  //       setProducts1([]);
  //     } finally {
  //       setLoadingProducts1(false);
  //     }
  //   };

  //   fetchProducts1();
  // }, [formData.diagnosis1]);

  // Mobile number format validation helper
  const isValidMobileFormat = (mobile) => {
    const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number format
    return mobileRegex.test(mobile);
  };

  // Real-time mobile validation - Updated to show list of matches and auto-link flag
  const validateMobile = async (mobile) => {
    if (!mobile || mobile.length < 10) {
      setMobileValidation({
        isValidating: false,
        isValid: null,
        message: "",
        existingPatients: [],
        autoLinkOnCreate: false,
        lead: null,
        hasLead: false
      });
      setLeadId(null); // Clear lead_id when mobile is cleared
      return;
    }

    if (!isValidMobileFormat(mobile)) {
      setMobileValidation({
        isValidating: false,
        isValid: false,
        message: "Please enter a valid 10-digit mobile number",
        existingPatients: [],
        autoLinkOnCreate: false
      });
      return;
    }

    setMobileValidation(prev => ({ ...prev, isValidating: true }));

    try {
      const response = await fetch(`https://newerp.awasthivitality.com/api/validate-mobile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile })
      });

      const result = await response.json();

      if (result.exists === true) {
        setMobileValidation({
          isValidating: false,
          isValid: true,
          message: result.message || "This mobile already exists. Relative will be auto-created.",
          existingPatients: Array.isArray(result.matches) ? result.matches : [],
          autoLinkOnCreate: !!result.auto_link_on_create,
          lead: result.lead || null,
          hasLead: !!result.lead
        });
        // Store lead_id if lead exists
        if (result.lead && result.lead.lead_id) {
          setLeadId(result.lead.lead_id);
        } else {
          setLeadId(null);
        }
      } else {
        setMobileValidation({
          isValidating: false,
          isValid: true,
          message: result.message || "Mobile number is available",
          existingPatients: [],
          autoLinkOnCreate: false,
          lead: result.lead || null,
          hasLead: !!result.lead || !!result.has_lead
        });
        // Store lead_id if lead exists
        if (result.lead && result.lead.lead_id) {
          setLeadId(result.lead.lead_id);
        } else {
          setLeadId(null);
        }
      }
    } catch (error) {
      setMobileValidation({
        isValidating: false,
        isValid: null,
        message: "Error validating mobile number",
        existingPatients: [],
        autoLinkOnCreate: false
      });
    }
  };

  // Debounced mobile validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.mobile) validateMobile(formData.mobile);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.mobile]);


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
    // REMOVE the formDataToSend.append block from here!
  };
 const handleProfileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setFormData((prev) => ({
    ...prev,
    patient_pic: file
  }));
};
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "radio") {
      if (name === "bp" || name === "diabetes" || name === "thyroid") {
        setFormData((prev) => ({ ...prev, [name]: parseInt(value) }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }


    if (name === "mobile") {
      setMobileValidation({
        isValidating: false,
        isValid: null,
        message: "",
        existingPatients: [],
        autoLinkOnCreate: false
      });
    }

    if (name === "diagnosis") {
      setFormData(prev => ({ ...prev, diagnosisSymptom: "", selectedSymptoms: [] }));
    }

    if (name === "diagnosis1") {
      setFormData(prev => ({ ...prev, diagnosisSymptom1: "", selectedSymptoms1: [] }));
    }
  };

  // Validation function - only format/required checks
  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Patient Name is required.";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required.";
    if (!formData.age) newErrors.age = "Age is required.";
    if (!formData.city) newErrors.city = "City is required.";
    if (!formData.state) newErrors.state = "State is required.";
    // if (!formData.diagnosis) newErrors.diagnosis = "Diagnosis is required.";
    if (!formData.serviceType) newErrors.serviceType = "Mode is required.";
    if (mobileValidation.isValid === false && mobileValidation.message.includes("valid 10-digit")) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      // Frontend permission guard (mirrors backend)
      const canCreate = await hasPermission('operation', 'addpatiententry');
      if (!canCreate) {
        alert('You do not have permission to create patient entries.');
        return;
      }

      const formDataToSend = new FormData();
      if (selectedImage) {
        // The backend looks specifically for 'image' or 'doc_patient'
        formDataToSend.append('image', selectedImage);
      }

      // Add all form fields with CORRECT field names
      formDataToSend.append('patient_type', formData.patientType);
      formDataToSend.append('patient_date', formData.patient_date);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('mobile', formData.mobile);
      formDataToSend.append('mobile2', formData.mobile2 || '');
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('age', formData.age);
      formDataToSend.append('sex', formData.sex);
      formDataToSend.append('weight', formData.weight || '0');
      formDataToSend.append('height', formData.height || '0');
      formDataToSend.append('patient_pic', formData.patient_pic || '0');

      formDataToSend.append('bp', formData.bp);
      formDataToSend.append('diabetes', formData.diabetes);
      formDataToSend.append('thyroid', formData.thyroid);
      formDataToSend.append('remark', formData.remark || '');
      formDataToSend.append('remark4', formData.remark4 || '');
      formDataToSend.append('remark1', formData.remark1 || '');
      formDataToSend.append('remark4_1', formData.remark4_1 || '');
      formDataToSend.append('address', formData.address || '');
      formDataToSend.append('pincode', formData.pincode || '');
      formDataToSend.append('city', formData.city);
      formDataToSend.append('state', formData.state);
      formDataToSend.append('country', formData.country || 'India');
      formDataToSend.append('service_type', formData.serviceType);
      formDataToSend.append('diagnosis_id', formData.diagnosis || '');
      formDataToSend.append(
        'medicine_names',
        JSON.stringify(formData.diagnosis ? selectedMedicines : [])
      );

      formDataToSend.append(
        'medicine_names1',
        JSON.stringify(formData.diagnosis1 ? selectedMedicines1 : [])
      );

      // Get first selected symptom ID for diagnosis_symptom_id (single ID, not array)
      const firstSymptomId = formData.selectedSymptoms?.length > 0 ? formData.selectedSymptoms[0] : 0;
      const firstSymptomId1 = formData.selectedSymptoms1?.length > 0 ? formData.selectedSymptoms1[0] : 0;

      // Only append if we have a valid symptom ID (not 0)
      if (firstSymptomId > 0) {
        formDataToSend.append('diagnosis_symptom_id', firstSymptomId);
      } else {
        formDataToSend.append('diagnosis_symptom_id', '0');
      }

      // formDataToSend.append('diagnosis_code', formData.diagnosisCode || '');
      formDataToSend.append('diagnosis_id1', formData.diagnosis1 || '');

      // Only append if we have a valid symptom ID for second diagnosis (not 0)
      if (firstSymptomId1 > 0) {
        formDataToSend.append('diagnosis_symptom_id1', firstSymptomId1);
      } else {
        formDataToSend.append('diagnosis_symptom_id1', '0');
      }

      // formDataToSend.append('diagnosis_code1', formData.diagnosisCode1 || '');
      formDataToSend.append(
        "medicine_names",
        JSON.stringify(selectedMedicines)
      );

      // Symptoms as JSON arrays
      formDataToSend.append('sign_symptom_all', JSON.stringify(formData.selectedSymptoms || []));
      formDataToSend.append('sign_symptom_all1', JSON.stringify(formData.selectedSymptoms1 || []));

      // formDataToSend.append('document', '');
      formDataToSend.append('consultency_fee', '0');
      formDataToSend.append('gst', '0');
      formDataToSend.append('pay_mode', '');
      formDataToSend.append('payment_status', '');
      formDataToSend.append('total_amt', '0');
      formDataToSend.append('username', '');
      formDataToSend.append('password', '');

      // Add lead_id for auto-transfer if found
      if (leadId) {
        formDataToSend.append('lead_id', leadId);
      }

      // Don't send patient_assign from frontend - let backend handle it automatically
      // The backend will get the logged-in user name from the Authorization token
      // formDataToSend.append('patient_assign', ''); // Removed - backend handles this
      formDataToSend.append('patient_otp', '0');
      formDataToSend.append('patient_fcm', '');

      // if (selectedImage) {
      //   formDataToSend.append('image', selectedImage);
      // }

      const token = getToken();
      const url = `https://newerp.awasthivitality.com/api/patient/entry${token ? `?token=${encodeURIComponent(token)}` : ''}`;

      const response = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: formDataToSend,
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.success) {

        // 🔍 SAFELY extract values from ANY backend format
        const patientName =
          result.data?.name ||
          result.name ||
          formData.name || "N/A";

        const patientId =
          result.data?.id ||
          result.data?.patient_id ||
          result.patient_id ||
          "-";

        const prNo =
          result.data?.pr_no ||
          result.pr_no ||
          "-";

        let msg = `✅ Patient Created Successfully!\n\n`;
        msg += `👤 Patient Name: ${patientName}\n`;
        // msg += `🆔 Patient ID: ${patientId}\n`;
        // msg += `📄 PR No: ${prNo}`;

        if (mobileValidation.autoLinkOnCreate) {
          msg += `\n\n👥 Existing mobile detected.\nRelative records have been auto-linked.`;
        }

        if (leadId || mobileValidation.hasLead) {
          msg += `\n\n🎯 Lead has been successfully transferred to this patient.`;
        }

        alert(msg);
        navigate("/PatientList");
      }

      else {
        if (response.status === 422) {
          // Validation errors
          let errorMessage = result.message || 'Validation failed:\n';
          if (result.errors) {
            const errorList = Object.entries(result.errors)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('\n');
            errorMessage += errorList;
          }
          alert(errorMessage);
        } else if (response.status === 403) {
          alert(`${result.message || 'Access denied.'}${result.required_permission ? `\nRequired: ${result.required_permission}` : ''}`);
        } else if (response.status === 401) {
          alert(result.message || 'Unauthorized - please login again.');
        } else {
          alert(result.message || `Failed to save patient (HTTP ${response.status}).`);
        }
        console.error("API Error:", result);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Server error, please try again.");
    }
  };
  const toggleSection = (section) => {
    if (section === "patient") setIsPatientInfoOpen((v) => !v);
    if (section === "diagnosis") setIsDiagnosisInfoOpen((v) => !v);
    if (section === "address") setIsAddressInfoOpen((v) => !v);
  };
const [transferring, setTransferring] = useState(false);

const transferLead = async (leadId) => {
  try {
    setTransferring(true);

    const response = await fetch(
      `https://newerp.awasthivitality.com/api/admin/lead/transfer/${leadId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        }
      }
    );

    const data = await response.json();

    if (data.success) {
      alert("Lead transferred successfully!");

      // ✅ Redirect to Patient List page
      navigate("/PatientList");

    } else {
      alert(data.msg || "Transfer failed");
    }

  } catch (error) {
    console.error(error);
    alert("Server error");
  } finally {
    setTransferring(false);
  }
};
  return (
    <div className="app-container">
      <style>{`
        :root{
          --primary-bg-color: #f6f8fb;
          --main-card-bg-color: #ffffff;
          --main-card-border-color: #e6e9ef;
          --main-card-shadow: 0 16px 30px rgba(15,23,42,0.06);
          --header-border-color: #f1f4f3;
          --header-title-color: #0f172a;
          --text-color: #374151;
          --accent-color: #06b6d4;
          --button-gradient-start: #38bdf8;
          --button-gradient-end: #2dd4bf;
          --button-hover-gradient-start: #0ea5e9;
          --button-hover-gradient-end: #14b8a6;
          --section-gradient-start: #06b6d4;
          --section-gradient-end: #f1f4f3;
          --submit-button-gradient-start: #fb923c;
          --submit-button-gradient-end: #f59e0b;
          --submit-button-hover-gradient-start: #f97316;
          --submit-button-hover-gradient-end: #eab308;
          --input-border-color: #d1d5db;
          --input-bg-color: #ffffff;
          --input-muted-bg: #f8fafc;
          --input-focus-color: rgba(6,182,212,0.18);
          --error-color: #ef4444;
          --success-color: #10b981;
          --warning-color: #f59e0b;
          --info-color: #3b82f6;
          --radius-sm: 6px;
          --radius-md: 12px;
        }

        *{box-sizing: border-box}
        body { font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; margin:0; padding:0; color:var(--text-color); }

        .app-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f6f8fb 0%, #eef2f7 50%, #f6f8fb 100%);
          background-size: 200% 200%;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 1rem;
        }

        @media (min-width: 640px) {
          .app-container { padding: 1.5rem; }
        }

        .main-card {
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          background: var(--main-card-bg-color);
          border-radius: var(--radius-md);
          box-shadow: var(--main-card-shadow);
          border: 1px solid var(--main-card-border-color);
          overflow: hidden;
        }

      .header {
  display: flex;
  flex-direction: column;
  gap: 0.15rem; /* smaller vertical spacing */
  padding: 0.5rem 1rem; /* 👈 reduced header height */
  border-bottom: 2px solid var(--header-border-color); /* thinner line */
  background: linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.95));
}


        @media (min-width: 640px) {
          .header { flex-direction: row; align-items: center; justify-content: space-between; }
        }

        .header-title { display:flex; align-items:center; gap:0.5rem; font-size:1.25rem; font-weight:800; color:var(--header-title-color); }
        .header-title .icon { color: var(--accent-color); }

        .header-nav { display:flex; gap:1rem; align-items:center; font-size:0.95rem; color:var(--text-color); }
        .header-nav .current-page { color: var(--accent-color); font-weight:700; }

        .header-button {
          background-image: linear-gradient(90deg, var(--button-gradient-start), var(--button-gradient-end));
          color: white;
          padding: 0.5rem 0.9rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-weight:600;
          box-shadow: 0 6px 18px rgba(3,105,161,0.06);
          transition: transform .18s ease, filter .18s ease;
        }
        .header-button:hover { transform: translateY(-2px); filter:brightness(1.02); }

        .main-content { padding: 1rem; }

        form { width:100%; }

        .form-meta {
          display:flex;
          gap:1rem;
          flex-direction:column;
          margin-bottom:1.25rem;
          align-items:flex-start;
        }
        @media (min-width:640px) {
          .form-meta { flex-direction:row; justify-content:space-between; align-items:flex-end; }
        }

        .label-group { display:flex; flex-direction:column; gap:0.5rem; }
        .label-text { font-weight:600; color:#0f172a; font-size:0.95rem; }

        .radio-group { display:flex; gap:1rem; align-items:center; color:var(--text-color); }
        .radio-label { display:flex; align-items:center; gap:0.5rem; cursor:pointer; user-select:none; }
        .radio-input { width:18px; height:18px; accent-color: var(--accent-color); }

        .date-input {
          padding:0.5rem;
          border-radius:8px;
          border:1px solid var(--input-border-color);
          background:var(--input-muted-bg);
          box-shadow: 0 1px 0 rgba(16,24,40,0.02);
        }
        .date-input:focus { outline: none; box-shadow: 0 0 0 4px var(--input-focus-color); border-color: var(--accent-color); }

        .form-section-container { margin-bottom:1.25rem; }
        .form-section-header {
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:1rem;
          padding:0.95rem 1rem;
          border-radius: 10px 10px 0 0;
          background-image: linear-gradient(90deg, var(--section-gradient-start), var(--section-gradient-end));
          color: white;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(3,105,161,0.06);
          transition: transform .14s ease, box-shadow .14s ease;
        }
        .form-section-header:hover { transform: translateY(-3px); }
        .header-content { display:flex; align-items:center; gap:0.6rem; }
        .section-title { font-weight:600; font-size:1rem; margin:0; }

        .form-section-body {
          padding:1.25rem;
          border:1px solid var(--main-card-border-color);
          border-top: none;
          background: var(--main-card-bg-color);
          border-radius: 0 0 10px 10px;
        }

        .form-section {
          background: linear-gradient(145deg, rgba(246, 248, 251, 0.5), rgba(255, 255, 255, 0.95));
          border-radius: 12px;
          // padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(230, 233, 239, 0.8);
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
        }

        .form-section-title {
          font-size: 1.5rem;
          font-weight: 900;
          color: var(--accent-color);
          margin: 0 0 1.5rem 0;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 1.5rem;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(8, 145, 178, 0.1) 100%);
          border-left: 5px solid var(--accent-color);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(6, 182, 212, 0.15);
          text-align: left;
        }

        .form-section-title .section-icon {
          width: 28px;
          height: 28px;
          color: var(--accent-color);
        }

        .diagnosis-block {
          padding: 1.5rem;
          background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(246, 248, 251, 0.6));
          border-radius: 12px;
          border: 2px solid rgba(6, 182, 212, 0.2);
          box-shadow: 0 6px 20px rgba(6, 182, 212, 0.12);
          margin-bottom: 1.5rem;
        }

      .form-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  align-items: flex-start;
}

.form-grid > div {
  display: flex;
  flex-direction: column;
  min-width: 0; /* prevents grid overflow issues */
}

.form-field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}

.form-field-input,
.form-field-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}

.required-star {
  color: red;
}

/* Responsive layout for smaller devices */
@media (max-width: 992px) {
  .form-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .form-grid > div[style*="grid-column: span 2"] {
    grid-column: span 2;
  }
}

@media (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid > div[style*="grid-column: span 2"] {
    grid-column: span 1;
  }
}

        .form-field-label { display:block; margin-bottom:0.35rem; font-weight:600; color:var(--text-color); font-size:0.92rem; }
        .form-field-input,
        .form-field-select,
        .form-field-textarea {
          width:100%;
          padding:0.55rem;
          border-radius:8px;
          border:1px solid var(--input-border-color);
          background:var(--input-bg-color);
          transition: box-shadow .16s ease, border-color .16s ease;
          font-size:0.95rem;
        }
        .form-field-select { background: #fff; }
        .form-field-input:focus,
        .form-field-select:focus,
        .form-field-textarea:focus {
          outline:none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 5px var(--input-focus-color);
        }
        .required-star { color: var(--error-color); margin-left:4px; font-weight:700; }

        .radio-field-group { display:flex; flex-wrap:wrap; gap:1rem; margin-top:0.35rem; }
        .radio-field-label { display:flex; align-items:center; gap:0.55rem; cursor:pointer; }

        .textarea-field { min-height:72px; resize:vertical; }

        .info-note { font-size:0.8rem; color:#6b7280; margin-top:0.35rem; }

        .section-block {
          display:flex;
          gap:0.75rem;
          align-items:center;
          padding:0.9rem 1rem;
          border-radius:10px;
          background-image: linear-gradient(90deg, rgba(6,182,212,0.08), rgba(16,185,129,0.06));
          border:1px solid rgba(16,185,129,0.06);
          margin-bottom:1rem;
        }

        .upload-reports-container {
          padding:1rem;
          border:1px solid var(--main-card-border-color);
          background:var(--main-card-bg-color);
          border-radius:0 0 10px 10px;
          margin-bottom:1rem;
        }
        .upload-reports-container input[type="file"] { color: #374151; }

        .image-upload-container {
          padding: 0;
          background: transparent;
          border-radius: 8px;
          margin-bottom: 0;
        }

        .image-upload-wrapper { display: flex; flex-direction: column; gap: 1rem; }
        .image-preview { max-width: 200px; max-height: 200px; border-radius: 8px; border: 2px solid var(--input-border-color); object-fit: cover; }
        .image-upload-input { padding: 0.5rem; border: 1px solid var(--input-border-color); border-radius: 8px; background: var(--input-bg-color); color: var(--text-color); }
        .image-upload-input:focus { outline: none; border-color: var(--accent-color); box-shadow: 0 0 0 3px var(--input-focus-color); }

        .mobile-validation { margin-top: 0.5rem; font-size: 0.85rem; display: flex; align-items: center; gap: 0.5rem; }
        .mobile-validation.loading { color: var(--warning-color); }
        // .mobile-validation.success { color: var(--success-color); }
        .mobile-validation.info { color: var(--info-color); }
        // .mobile-validation.error { color: var(--error-color); }
        .mobile-validation-icon { width: 16px; height: 16px; }

        .existing-patient-info {
          margin-top: 0.75rem;
          padding: 1rem;
          background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%);
          border: 2px solid #3b82f6;
          border-left: 5px solid #3b82f6;
          border-radius: 12px;
          font-size: 0.85rem;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
          width: 100%;
        }
        .existing-patient-info h4 { 
          margin: 0 0 0.75rem 0; 
          color: #1e40af; 
          font-size: 1rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid rgba(59, 130, 246, 0.2);
        }
        .existing-patient-info .patients-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }
        .existing-patient-info .patient-card {
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 8px;
          border: 1px solid rgba(59, 130, 246, 0.2);
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.1);
          transition: all 0.2s ease;
        }
        .existing-patient-info .patient-card:hover {
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.15);
          transform: translateY(-1px);
        }
        .existing-patient-info .patient-card p {
          margin: 0.25rem 0;
          color: #1e3a8a;
          line-height: 1.4;
          font-size: 0.85rem;
        }
        .existing-patient-info .patient-card p strong {
          color: #1e40af;
          font-weight: 700;
          margin-right: 0.4rem;
        }
        .existing-patient-info .note-message {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 8px;
          border-left: 4px solid #10b981;
          color: #065f46;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .existing-patient-info .note-message strong {
          color: #059669;
        }
        
        @media (max-width: 768px) {
          .existing-patient-info .patients-grid {
            grid-template-columns: 1fr;
          }
        }

        .loading-text { color: var(--warning-color); font-style: italic; }

        .submit-button-container { display:flex; justify-content:flex-end; padding:1rem 0; background:transparent; border-radius:0 0 12px 12px; }
        .submit-button {
          background-image: linear-gradient(90deg, var(--submit-button-gradient-start), var(--submit-button-gradient-end));
          color:white;
          padding:0.7rem 1.6rem;
          border-radius:10px;
          border:none;
          font-weight:800;
          cursor:pointer;
          box-shadow: 0 12px 30px rgba(249,115,22,0.10);
          transition: transform .14s ease, filter .14s ease;
        }
        .submit-button:hover { transform: translateY(-3px); filter:brightness(1.02); }

        .error-message { color: var(--error-color); font-size: 0.85rem; margin-top: 0.25rem; }

        .symptoms-container {
          margin-top: 1.5rem;
          padding: 1.5rem;
          border: 2px solid rgba(6, 182, 212, 0.2);
          border-radius: 12px;
          background: linear-gradient(145deg, rgba(246, 248, 251, 0.8), rgba(255, 255, 255, 0.95));
          box-shadow: 0 4px 12px rgba(6, 182, 212, 0.1);
        }

        .symptoms-container h3 {
          font-size: 1.2rem;
          font-weight: 800;
          color: var(--accent-color);
          margin: 0 0 1rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid rgba(6, 182, 212, 0.2);
        }

        .symptoms-container h3 .section-icon {
          width: 24px;
          height: 24px;
          color: var(--accent-color);
        }
        .symptoms-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.75rem;
          margin-top: 0.75rem;
        }
        .symptom-checkbox-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; padding: 0.5rem; border-radius: 6px; transition: background-color 0.2s ease; }
        .symptom-checkbox-label:hover { background-color: rgba(6, 182, 212, 0.05); }
        .symptom-checkbox { width: 16px; height: 16px; accent-color: var(--accent-color); }

        .horizontal-form { 
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          align-items: flex-start;
          // padding: 1rem;
          background: linear-gradient(145deg, rgba(249, 251, 253, 0.6), rgba(255, 255, 255, 0.95));
          border-radius: 10px;
          border: 1px solid rgba(230, 233, 239, 0.6);
          margin-bottom: 1.5rem;
        }
        
        @media (max-width: 768px) {
          .horizontal-form {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
        .form-item { display: flex; flex-direction: column; }
        .two-column-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

        .add-diagnosis-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin: 2rem auto;
          padding: 0;
        }

        .add-diagnosis-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          // padding: 0.875rem 1.75rem;
          background: linear-gradient(135deg, var(--accent-color) 0%, #0891b2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(6, 182, 212, 0.3);
          transition: all 0.3s ease;
          min-width: 220px;
        }

        .add-diagnosis-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(6, 182, 212, 0.4);
          background: linear-gradient(135deg, #0891b2 0%, var(--accent-color) 100%);
        }

        .add-diagnosis-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(6, 182, 212, 0.3);
        }

        .add-diagnosis-btn .btn-icon {
          width: 20px;
          height: 20px;
        }
        .symptom-text { font-size: 0.9rem; color: var(--text-color); }
        /* 👇 Make Age field narrower */
.small-age-input {
  width: 80px;
}
  .highlight-box {
  background: linear-gradient(145deg, #f9fbfd, #f0f4f8);
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.75rem;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
  transition: all 0.3s ease;
}

.highlight-box:hover {
  background: linear-gradient(145deg, #f8fafc, #edf2f7);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.1);
}
  .patient-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
}

.patient-info-row > div {
  display: flex;
  flex-direction: column;
  width: auto;
}

.patient-info-row .form-field-input,
.patient-info-row .form-field-select {
  width: 127px; /* 👈 small input boxes */
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}

.patient-info-row .form-field-label {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
}

.address-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;              /* spacing between fields */
  align-items: flex-end;  /* aligns labels nicely at bottom */
}

.address-info-row > div {
  display: flex;
  flex-direction: column;
  width: auto;
}

.address-info-row .form-field-input,
.address-info-row .form-field-select {
  width: 140px; /* 👈 small input width */
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}

/* Make the Address field a bit wider */
.address-info-row input[name="address"] {
  width: 500px;
}

.address-info-row .form-field-label {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
}
.diagnosis-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap; /* allows responsiveness */
}

.diagnosis-row > div {
  flex: 1;
  min-width: 200px;
}

/* ------------------------------------------ */
/* MOBILE RESPONSIVE (ONLY FOR < 768px)       */
/* ------------------------------------------ */
@media (max-width: 768px) {

  .main-card {
    padding: 10px !important;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .form-grid,
  .patient-info-row,
  .diagnosis-row,
  .address-info-row {
    display: grid !important;
    grid-template-columns: 1fr !important;   /* Convert to single column */
    gap: 15px !important;
  }

  /* Input full width */
  .form-field-input,
  .form-field-select,
  .date-input {
    width: 100% !important;
  }

  /* Radio groups vertical */
  .horizontal-form {
    flex-direction: column !important;
    gap: 20px !important;
  }

  .radio-field-group {
    flex-direction: row;
    gap: 15px;
    flex-wrap: wrap;
  }

  /* Symptoms grid adjust */
  .symptoms-grid {
    grid-template-columns: 1fr !important;
  }

  /* Service mode buttons */
  .form-section div[style*="justify-content: space-between"] {
    flex-direction: column !important;
    gap: 10px !important;
    align-items: flex-start !important;
  }

  /* Existing patient cards single column */
  .patients-grid {
    grid-template-columns: 1fr !important;
  }

  .image-preview {
    width: 100% !important;
    max-height: 280px;
    object-fit: cover;
  }

  .submit-button-container {
    text-align: center;
  }

  .submit-button {
    width: 100%;
  }
}

      `}</style>
      <div className="main-card ">
        <header className="header">
          <h1 className="header-title">
            <PlusSquare className="icon" />
            New Patient Entry
          </h1>
          <div className="form-meta">
            <div>
              <label className="label-text">Entry Date</label>
              <input
                type="date"
                name="patient_date"
                value={formData.patient_date}
                onChange={handleChange}
                className="date-input"
              />
            </div>
          </div>
        </header>

        <main className="main-content">
          <form onSubmit={handleSubmit}>
            <div className="form-section ">
              <h2 className="form-section-title">
                <User className="section-icon" size={28} />
                Patient Information
              </h2>
              <div className="form-grid patient-info-row">
                <div>
                  <label className="form-field-label ">Patient Name <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Name"
                    className="form-field-input"
                  />
                  {errors.name && <p className="error-message">{errors.name}</p>}
                </div>

                <div>
                  <label className="form-field-label">Mobile <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter Mobile"
                    className="form-field-input"
                    maxLength="10"
                  />
                  {errors.mobile && <p className="error-message">{errors.mobile}</p>}

                  {mobileValidation.isValidating && (
                    <div className="mobile-validation loading">
                      <div className="mobile-validation-icon">⏳</div>
                      <span>Validating mobile number...</span>
                    </div>
                  )}

                  {mobileValidation.isValid === true && mobileValidation.existingPatients.length === 0 && (
                    <div className="mobile-validation">
                      <div className="mobile-validation-icon">✅</div>
                      <span>{mobileValidation.message}</span>
                    </div>
                  )}

                  {mobileValidation.isValid === true && mobileValidation.existingPatients.length > 0 && (
                    <div className="mobile-validation info">
                      <div className="mobile-validation-icon">ℹ️</div>
                      <span>{mobileValidation.message}</span>
                    </div>
                  )}

                  {mobileValidation.isValid === false && (
                    <div className="mobile-validation error">
                      <div className="mobile-validation-icon">❌</div>
                      <span>{mobileValidation.message}</span>
                    </div>
                  )}

                  {Array.isArray(mobileValidation.existingPatients) && mobileValidation.existingPatients.length > 0 && (
                    <div className="existing-patient-info">
                      <h4>👥 Existing Patients Found ({mobileValidation.existingPatients.length})</h4>
                      <div className="patients-grid">
                        {mobileValidation.existingPatients.map((p) => (
                          <div key={p.patient_id} className="patient-card">
                            <p><strong>Patient ID:</strong> {p.patient_id} | <strong>PR No:</strong> {p.pr_no}</p>
                            <p><strong>Name:</strong> {p.name} {p.age ? `| Age: ${p.age}` : ''} {p.sex ? `| Sex: ${p.sex}` : ''}</p>
                            <p><strong>City/State:</strong> {p.city}{p.state ? `, ${p.state}` : ''}</p>
                            <p><strong>Mobile(s):</strong> {[p.mobile, p.mobile2, p.mobile3].filter(Boolean).join(', ')}</p>
                          </div>
                        ))}
                      </div>
                      {mobileValidation.autoLinkOnCreate && (
                        <div className="note-message">
                          <strong>Note:</strong> A family relationship will be created automatically on save.
                        </div>
                      )}
                    </div>
                  )}

                {(mobileValidation.lead || mobileValidation.hasLead) && (
  <div
    className="existing-patient-info"
    style={{
      marginTop: mobileValidation.existingPatients?.length > 0 ? "1rem" : "0",
      backgroundColor: "#fef3c7",
      border: "1px solid #fbbf24",
    }}
  >
    <h4 style={{ marginTop: 0, color: "#92400e" }}>🎯 Lead Found</h4>

    {mobileValidation.lead ? (
      <div
        style={{
          marginBottom: "8px",
          paddingBottom: "8px",
          borderBottom: "1px dashed #fbbf24",
        }}
      >
        <p><strong>Lead ID:</strong> {mobileValidation.lead.lead_id}</p>
        <p><strong>Name:</strong> {mobileValidation.lead.lead_name || "N/A"}</p>
        <p><strong>Phone:</strong> {mobileValidation.lead.lead_phone}</p>
        <p><strong>Reason:</strong> {mobileValidation.lead.lead_reason || "N/A"}</p>
        {/* <p><strong>Status:</strong> {mobileValidation.lead.lead_status == 1 ? "Win" : mobileValidation.lead.lead_status == 2 ? "Running" : "Lost"}</p> */}
       <p><strong>Status:</strong> {mobileValidation.lead.status_text}</p> 
        <p><strong>Source:</strong> {mobileValidation.lead.lead_source || "N/A"}</p>

        {mobileValidation.lead.lead_date && (
          <p>
            <strong>Date:</strong>{" "}
            {new Date(mobileValidation.lead.lead_date).toLocaleDateString()}
          </p>
        )}

        {/* ✅ BUTTON GOES HERE */}
        <button
          type="button"
          onClick={() => transferLead(mobileValidation.lead.lead_id)}
          disabled={transferring}
          style={{
            marginTop: "10px",
            padding: "8px 14px",
            background: "green",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          {transferring ? "Transferring..." : "🔄 Transfer Lead to Patient"}
        </button>
      </div>
    ) : (
      <p>Lead information is available but details are loading...</p>
    )}

    {mobileValidation.lead?.can_transfer && (
      <p
        style={{
          color: "#059669",
          fontWeight: "bold",
          marginTop: "0.5rem",
        }}
      >
        ✓ This lead will be automatically transferred to patient when you save.
      </p>
    )}
  </div>
)}
                </div>

                <div>
                  <label className="form-field-label">Whatsapp Number</label>
                  <input
                    type="text"
                    name="mobile2"
                    value={formData.mobile2}
                    onChange={handleChange}
                    placeholder="Enter Whatsapp"
                    className="form-field-input"
                    maxLength="10"
                  />
                </div>

                {/* <div>
                  <label className="form-field-label">Email Id</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Email Id"
                    className="form-field-input"
                  />
                </div> */}

                <div>
                  <label className="form-field-label">Age <span className="required-star">*</span></label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    placeholder="Enter Age"
                    className="form-field-input "
                    min="0"
                    max="120"
                  />
                  {errors.age && <p className="error-message">{errors.age}</p>}
                </div>

                <div>
                  <label className="form-field-label">Sex</label>
                  <select
                    name="sex"
                    value={formData.sex}
                    onChange={handleChange}
                    className="form-field-select"
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                </div>

                <div>
                  <label className="form-field-label">Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="Enter Weight"
                    className="form-field-input"
                  />
                </div>

                <div>
                  <label className="form-field-label">Height</label>
                  <input
                    type="text"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="Enter Height"
                    className="form-field-input"
                  />
                </div>

              <div>
  <label className="form-field-label">Upload Patient Profile</label>

  <input
    type="file"
    name="patient_pic"
    accept="image/*"
    onChange={handleProfileChange}
    className="form-field-input"
  />

  {formData.patient_pic && (
    <div style={{ marginTop: "10px" }}>
      <img
        src={
          typeof formData.patient_pic === "string"
            ? formData.patient_pic
            : URL.createObjectURL(formData.patient_pic)
        }
        alt="Patient Preview"
        width="120"
        style={{
          borderRadius: "8px",
          border: "1px solid #ddd",
          padding: "4px"
        }}
      />
    </div>
  )}
</div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <Heart className="section-icon" size={28} />
                Medical History
              </h2>
              <div className="horizontal-form">
                <div className="form-item">
                  <label className="form-field-label">
                    BP <span className="required-star">*</span>
                  </label>
                  <div className="radio-field-group">
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="bp"
                        value={1}
                        checked={formData.bp === 1}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="bp"
                        value={0}
                        checked={formData.bp === 0}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                <div className="form-item">
                  <label className="form-field-label">
                    Diabetes <span className="required-star">*</span>
                  </label>
                  <div className="radio-field-group">
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="diabetes"
                        value={1}
                        checked={formData.diabetes === 1}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="diabetes"
                        value={0}
                        checked={formData.diabetes === 0}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                <div className="form-item">
                  <label className="form-field-label">
                    Thyroid <span className="required-star">*</span>
                  </label>
                  <div className="radio-field-group">
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="thyroid"
                        value={1}
                        checked={formData.thyroid === 1}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="radio-field-label">
                      <input
                        type="radio"
                        name="thyroid"
                        value={0}
                        checked={formData.thyroid === 0}
                        onChange={handleChange}
                        className="radio-input"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <FileImage className="section-icon" size={28} />
                Patient Report
              </h2>
              <div className="image-upload-container">
                <div className="image-upload-wrapper">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="image-upload-input"
                  />
                  {imagePreview && (
                    <div>
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              {/* ---- First Diagnosis ---- */}
              <h2 className="form-section-title">
                <Stethoscope className="section-icon" size={28} />
                First Diagnosis
              </h2>

              <div className="form-grid diagnosis-row">
                <div>
                  <label className="form-field-label">
                    Diagnosis
                  </label>                  <select
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={handleChange}
                    className="form-field-select"
                    disabled={loadingDiagnoses}
                  >
                    <option value="">
                      {loadingDiagnoses ? "Loading diagnoses..." : "Select Diagnosis"}
                    </option>
                    {diagnoses.map((diagnosis) => (
                      <option key={diagnosis.diagnosis_id} value={diagnosis.diagnosis_id}>
                        {diagnosis.name}
                      </option>
                    ))}
                  </select>
                  {errors.diagnosis && <span className="error-message" style={{ color: 'red', fontSize: '12px' }}>{errors.diagnosis}</span>}
                </div>

                {/* <div>
                  <label className="form-field-label">Diagnosis Code</label>
                  <input
                    type="text"
                    name="diagnosisCode"
                    value={formData.diagnosisCode}
                    onChange={handleChange}
                    placeholder="Enter Code"
                    className="form-field-input"
                  />
                </div> */}

                <div>
                  <label className="form-field-label">Dosing</label>
                  <input
                    type="text"
                    name="remark"
                    value={formData.remark}
                    onChange={handleChange}
                    placeholder="Enter Dosing"
                    className="form-field-input"
                  />
                </div>
                <div>
                  <label className="form-field-label">Visit Remark</label>
                  <input
                    type="text"
                    name="remark4"
                    value={formData.remark4}
                    onChange={handleChange}
                    placeholder="Enter Visit Remark"
                    className="form-field-input"
                  />
                </div>
                {!showSecondDiagnosis && (
                  <div className="add-diagnosis-button">

                    <button
                      type="button"
                      onClick={() => setShowSecondDiagnosis(true)}
                      className="add-diagnosis-btn"
                    >
                      <CirclePlus size={24} strokeWidth={2.5} />Add More Diagnosis
                    </button>

                  </div>
                )}
              </div>
              {formData.diagnosis && (
                <div className="symptoms-container">
                  <h3>
                    <Activity className="section-icon" size={24} />
                    First Diagnosis - Sign & Symptoms
                  </h3>
                  {loadingSymptoms ? (
                    <div className="loading-text">Loading symptoms...</div>
                  ) : symptoms.length > 0 ? (
                    <div className="symptoms-grid">
                      {symptoms.map((symptom) => (
                        <label key={symptom.diagnosis_symptom_id} className="symptom-checkbox-label">
                          <input
                            type="checkbox"
                            className="symptom-checkbox"
                            checked={formData.selectedSymptoms?.includes(symptom.diagnosis_symptom_id)}
                            onChange={(e) => {
                              const selectedSymptoms = formData.selectedSymptoms || [];
                              if (e.target.checked) {
                                setFormData(prev => ({ ...prev, selectedSymptoms: [...selectedSymptoms, symptom.diagnosis_symptom_id] }));
                              } else {
                                setFormData(prev => ({ ...prev, selectedSymptoms: selectedSymptoms.filter(id => id !== symptom.diagnosis_symptom_id) }));
                              }
                            }}
                          />
                          <span className="symptom-text">{symptom.symptom}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="loading-text">No symptoms available for this diagnosis.</div>
                  )}

                </div>
              )}
              {/* 🔽 ADD THIS BLOCK IMMEDIATELY AFTER FIRST DIAGNOSIS SYMPTOMS */}
              {/* 🔽 FIRST DIAGNOSIS MEDICINES */}
              {/* FIRST DIAGNOSIS MEDICINES */}
              {formData.diagnosis && (
                <div className="symptoms-container">
                  <h3>💊 Medicines</h3>

                  {loadingProducts ? (
                    <p className="loading-text">Loading medicines...</p>
                  ) : products.length === 0 ? (
                    <p className="loading-text">No medicines available for this diagnosis</p>
                  ) : (
                    <div className="symptoms-grid">
                      {products.map((product) => (
                        <label key={product.product_name} className="symptom-checkbox-label">
                          <input
                            type="checkbox"
                            className="symptom-checkbox"
                            checked={selectedMedicines.includes(product.product_name)}
                            onChange={(e) => {
                              const id = product.product_name;
                              setSelectedMedicines((prev) =>
                                e.target.checked
                                  ? [...prev, id]
                                  : prev.filter((x) => x !== id)
                              );
                            }}
                          />
                          <span className="symptom-text">
                            {product.medicine_name || product.product_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Second Diagnosis ---- */}
              {showSecondDiagnosis && (
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid rgba(230, 233, 239, 0.8)' }}>
                  <h2 className="form-section-title">
                    <Stethoscope className="section-icon" size={28} />
                    Second Diagnosis
                  </h2>

                  <div className="form-grid">
                    <div>
                      <label className="form-field-label">Second Diagnosis</label>
                      <select
                        name="diagnosis1"
                        value={formData.diagnosis1}
                        onChange={handleChange}
                        className="form-field-select"
                        disabled={loadingDiagnoses}
                      >
                        <option value="">Select Second Diagnosis</option>
                        {diagnoses.map((diagnosis) => (
                          <option key={diagnosis.diagnosis_id} value={diagnosis.diagnosis_id}>
                            {diagnosis.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* <div>
                      <label className="form-field-label">Second Diagnosis Code</label>
                      <input
                        type="text"
                        name="diagnosisCode1"
                        value={formData.diagnosisCode1}
                        onChange={handleChange}
                        placeholder="Enter Second Diagnosis Code"
                        className="form-field-input"
                      />
                    </div> */}
                    <div>
                      <label className="form-field-label">Dosing</label>
                      <input
                        type="text"
                        name="remark1"
                        value={formData.remark1}
                        onChange={handleChange}
                        className="form-field-input"
                      />
                    </div>
                    <div>
                      <label className="form-field-label">Visit Remark</label>
                      <input
                        type="text"
                        name="remark4_1"
                        value={formData.remark4_1}
                        onChange={handleChange}
                        placeholder="Enter Visit Remark"
                        className="form-field-input"
                      />
                    </div>
                  </div>

                  {formData.diagnosis1 && (
                    <div className="symptoms-container">
                      <h3>
                        <Activity className="section-icon" size={24} />
                        Second Diagnosis - Sign & Symptoms
                      </h3>
                      {loadingSymptoms1 ? (
                        <div className="loading-text">Loading symptoms...</div>
                      ) : symptoms1.length > 0 ? (
                        <div className="symptoms-grid">
                          {symptoms1.map((symptom) => (
                            <label key={symptom.diagnosis_symptom_id} className="symptom-checkbox-label">
                              <input
                                type="checkbox"
                                className="symptom-checkbox"
                                checked={formData.selectedSymptoms1?.includes(symptom.diagnosis_symptom_id)}
                                onChange={(e) => {
                                  const selectedSymptoms1 = formData.selectedSymptoms1 || [];
                                  if (e.target.checked) {
                                    setFormData(prev => ({ ...prev, selectedSymptoms1: [...selectedSymptoms1, symptom.diagnosis_symptom_id] }));
                                  } else {
                                    setFormData(prev => ({ ...prev, selectedSymptoms1: selectedSymptoms1.filter(id => id !== symptom.diagnosis_symptom_id) }));
                                  }
                                }}
                              />
                              <span className="symptom-text">{symptom.symptom}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="loading-text">No symptoms available for this diagnosis.</div>
                      )}

                    </div>
                  )}
                  {/* 🔽 ADD THIS BLOCK IMMEDIATELY AFTER SECOND DIAGNOSIS SYMPTOMS */}
                  {/* 🔽 SECOND DIAGNOSIS MEDICINES */}
                  {formData.diagnosis1 && (
                    <div className="symptoms-container">
                      <h3>💊 Medicines</h3>

                      {loadingProducts1 ? (
                        <p className="loading-text">Loading medicines...</p>
                      ) : products1.length === 0 ? (
                        <p className="loading-text">No medicines available</p>
                      ) : (
                        <div className="symptoms-grid">
                          {products1.map((product) => (
                            <label key={product.product_name} className="symptom-checkbox-label">
                              <input
                                type="checkbox"
                                className="symptom-checkbox"
                                checked={selectedMedicines1.includes(product.product_name)}
                                onChange={(e) => {
                                  const id = product.product_name;
                                  setSelectedMedicines1((prev) =>
                                    e.target.checked
                                      ? [...prev, id]
                                      : prev.filter((x) => x !== id)
                                  );
                                }}
                              />
                              <span className="symptom-text">
                                {product.medicine_name || product.product_name}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <MapPin className="section-icon" size={28} />
                Address Information
              </h2>
              <div className="form-grid address-info-row">
                <div style={{ gridColumn: "span 2" }}>
                  <label className="form-field-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter Patient Address"
                    className="form-field-input"
                  />
                </div>

                <div>
                  <label className="form-field-label">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Enter Pincode"
                    className="form-field-input"
                    maxLength="6"
                    pattern="[0-9]{6}"
                  />
                </div>

                <div>
                  <label className="form-field-label">City <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter Patient City"
                    className="form-field-input"
                  />
                  {errors.city && <p className="error-message">{errors.city}</p>}
                </div>

                <div>
                  <label className="form-field-label">State <span className="required-star">*</span></label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="form-field-select"
                  >
                    <option value="">Select</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chandigarh">Chandigarh</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jammu & Kashmir">Jammu & Kashmir</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Lakshadweep">Lakshadweep</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Puducherry">Puducherry</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.state && <p className="error-message">{errors.state}</p>}
                </div>

                <div>
                  <label className="form-field-label">Country <span className="required-star">*</span></label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="form-field-select"
                  >
                    <option value="India">India</option>
                    <option value="Afghanistan">Afghanistan</option>
                    <option value="Albania">Albania</option>
                    <option value="Algeria">Algeria</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Australia">Australia</option>
                    <option value="Austria">Austria</option>
                    <option value="Bahrain">Bahrain</option>
                    <option value="Bangladesh">Bangladesh</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Canada">Canada</option>
                    <option value="China">China</option>
                    <option value="Denmark">Denmark</option>
                    <option value="Egypt">Egypt</option>
                    <option value="Finland">Finland</option>
                    <option value="France">France</option>
                    <option value="Germany">Germany</option>
                    <option value="Greece">Greece</option>
                    <option value="Hong Kong">Hong Kong</option>
                    <option value="Indonesia">Indonesia</option>
                    <option value="Iran">Iran</option>
                    <option value="Iraq">Iraq</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Israel">Israel</option>
                    <option value="Italy">Italy</option>
                    <option value="Japan">Japan</option>
                    <option value="Jordan">Jordan</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Kuwait">Kuwait</option>
                    <option value="Malaysia">Malaysia</option>
                    <option value="Maldives">Maldives</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Nepal">Nepal</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Norway">Norway</option>
                    <option value="Oman">Oman</option>
                    <option value="Pakistan">Pakistan</option>
                    <option value="Philippines">Philippines</option>
                    <option value="Poland">Poland</option>
                    <option value="Portugal">Portugal</option>
                    <option value="Qatar">Qatar</option>
                    <option value="Russia">Russia</option>
                    <option value="Saudi Arabia">Saudi Arabia</option>
                    <option value="Singapore">Singapore</option>
                    <option value="South Africa">South Africa</option>
                    <option value="South Korea">South Korea</option>
                    <option value="Spain">Spain</option>
                    <option value="Sri Lanka">Sri Lanka</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Thailand">Thailand</option>
                    <option value="Turkey">Turkey</option>
                    <option value="Ukraine">Ukraine</option>
                    <option value="United Arab Emirates">United Arab Emirates</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                    <option value="Vietnam">Vietnam</option>
                    <option value="Yemen">Yemen</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <FileText className="section-icon" size={28} />
                Service Mode
              </h2>
              <div>
                {/* <label className="form-field-label">
                  Mode <span className="required-star">*</span>
                </label> */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  {["VISIT", "COURIER", "SOMEONE", "VOICE", "VIDEO"].map((option) => (
                    <label key={option} style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap" }}>
                      <input
                        type="radio"
                        name="serviceType"
                        value={option}
                        checked={formData.serviceType === option}
                        onChange={handleChange}
                        style={{ marginRight: "6px" }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="submit-button-container">
              <button className="submit-button" type="submit">Submit</button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default NewEntry;